import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth/useAuth';

/**
 * Gate a subtree behind authentication.
 *
 * While the initial `/auth/status` request is in flight we render a minimal
 * loading state instead of flashing to `/login`. After it resolves, an
 * unauthenticated user is redirected with their current path stashed in
 * `?next=` so the login flow can return them.
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    const next = location.pathname + location.search + location.hash;
    const target = `/login?next=${encodeURIComponent(next)}`;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}

function AuthLoading(): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-screen items-center justify-center text-sm text-neutral-500"
    >
      Checking session…
    </div>
  );
}
